import cn from "classnames";
import debounce from "lodash-es/debounce";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  addPromptToHistory,
  getAiPromptDraft,
  getAiPromptHistory,
  setAiPromptDraft,
} from "pages/DevelopWorkspace/helpers/aiPromptHistory";
import {
  DocumentAiState,
  DocumentAiStateUpdate,
} from "pages/DevelopWorkspace/workspace.types";

import styles from "./AiAssistantWidgetView.module.scss";

type HistoryState = { entries: string[]; index: number; preDraft: string };

type Props = {
  aiState: DocumentAiState;
  mergeActive: boolean;
  onUpdatePanel: (updates: Partial<DocumentAiStateUpdate>) => void;
  onClose: () => void;
  onAction: (userPrompt: string) => void;
  onStop: () => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  requestMeasure: () => void;
};

function useTextareaAutosize(
  textareaRef: React.RefObject<HTMLTextAreaElement>
) {
  const adjust = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const MIN_HEIGHT = 18;
    const MAX_HEIGHT = 150;
    textarea.style.height = "auto";
    const nextHeight = Math.min(
      MAX_HEIGHT,
      Math.max(textarea.scrollHeight, MIN_HEIGHT)
    );
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = nextHeight >= MAX_HEIGHT ? "auto" : "hidden";
  }, [textareaRef]);

  return adjust;
}

export default function AiAssistantWidgetView(props: Props) {
  const {
    aiState,
    mergeActive,
    onUpdatePanel,
    onClose,
    onAction,
    onStop,
    onAcceptAll,
    onRejectAll,
    requestMeasure,
  } = props;

  const { t } = useTranslation();

  const rootRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const adjustTextarea = useTextareaAutosize(textareaRef);
  const debouncedUpdate = useMemo(
    () =>
      debounce((value: string) => {
        onUpdatePanel({ text: value });
      }, 100),
    [onUpdatePanel]
  );

  const [initialTextAtOpen] = useState<string>(aiState.text || "");
  const [userEditedSinceOpen, setUserEditedSinceOpen] =
    useState<boolean>(false);

  const [historyState, setHistoryState] = useState<HistoryState>(() => ({
    entries: getAiPromptHistory(),
    index: -1,
    preDraft: "",
  }));
  const primaryButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mergeActive) {
      primaryButtonRef.current?.focus();
    }
  }, [mergeActive]);

  const hasText = useMemo(() => !!(aiState.text || "").trim(), [aiState.text]);
  const disablePrimary = useMemo(
    () => !!aiState.isLoading || (!mergeActive && !hasText),
    [aiState.isLoading, mergeActive, hasText]
  );

  const primaryLabel = useMemo(() => {
    if (aiState.isLoading)
      return {
        text: aiState.sql
          ? t("editor.ai_widget.loading.modifying")
          : t("editor.ai_widget.loading.generating"),
        hint: null,
      };
    const hint = "⏎";
    if (mergeActive)
      return {
        text: t("editor.ai_widget.action.accept"),
        hint,
      };
    if (aiState.sql)
      return {
        text: t("editor.ai_widget.action.modify"),
        hint,
      };
    return {
      text: t("editor.ai_widget.action.generate"),
      hint,
    };
  }, [aiState.isLoading, aiState.sql, mergeActive, t]);

  const cancelLabel = useMemo(() => {
    const hint = "ESC";
    if (aiState.isLoading)
      return { text: t("editor.ai_widget.cancel.stop"), hint };
    if (mergeActive)
      return {
        text: t("editor.ai_widget.cancel.reject"),
        hint,
      };
    return {
      text: t("editor.ai_widget.cancel.cancel"),
      hint,
    };
  }, [aiState.isLoading, mergeActive, t]);

  const flushDebounce = useCallback(() => {
    debouncedUpdate.flush();
  }, [debouncedUpdate]);

  const handleTextareaInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.currentTarget.value;
      debouncedUpdate(value);
      setUserEditedSinceOpen(value !== initialTextAtOpen);
      setAiPromptDraft(value);
      adjustTextarea();
      requestMeasure();
    },
    [debouncedUpdate, initialTextAtOpen, adjustTextarea, requestMeasure]
  );

  const applyTextareaValue = useCallback(
    (value: string) => {
      const el = textareaRef.current;
      if (!el) return;
      el.value = value;
      onUpdatePanel({ text: value });
      setAiPromptDraft(value);
      setUserEditedSinceOpen(value !== initialTextAtOpen);
      const end = value.length;
      el.setSelectionRange(end, end);
      adjustTextarea();
      requestMeasure();
    },
    [onUpdatePanel, initialTextAtOpen, adjustTextarea, requestMeasure]
  );

  const handlePrimary = useCallback(() => {
    if (aiState.isLoading) return;
    if (mergeActive) {
      onAcceptAll();
      return;
    }
    // Ensure latest text is flushed before action
    flushDebounce();
    const currentValue = (
      textareaRef.current?.value ||
      aiState.text ||
      ""
    ).trim();
    if (!currentValue) return;

    const openedWithText = !!(initialTextAtOpen && initialTextAtOpen.trim());
    const shouldPersist =
      currentValue &&
      !(
        openedWithText &&
        !userEditedSinceOpen &&
        currentValue === initialTextAtOpen.trim()
      );
    if (shouldPersist) {
      addPromptToHistory(currentValue);
      setHistoryState(prev => ({ ...prev, entries: getAiPromptHistory() }));
    }
    onAction(currentValue);
  }, [
    aiState.isLoading,
    mergeActive,
    aiState.text,
    initialTextAtOpen,
    userEditedSinceOpen,
    onAcceptAll,
    onAction,
    flushDebounce,
  ]);

  const handleCancel = useCallback(() => {
    if (aiState.isLoading) {
      onStop();
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
      return;
    }
    if (mergeActive) {
      onRejectAll();
      onClose();
      return;
    }
    onClose();
  }, [aiState.isLoading, mergeActive, onStop, onRejectAll, onClose]);

  const handleRootKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        // Only when focus is inside
        if (!rootRef.current?.contains(document.activeElement)) return;
        e.preventDefault();
        handlePrimary();
      }
    },
    [handlePrimary]
  );

  const handleTextareaHistoryKeys = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (aiState.isLoading || mergeActive) return;
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey)
        return;

      const el = textareaRef.current;
      if (!el) return;
      if (el.selectionStart !== el.selectionEnd) return;

      const value = el.value;
      const pos = el.selectionStart;
      const atFirstLine = value.lastIndexOf("\n", Math.max(0, pos - 1)) === -1;
      const nextNewline = value.indexOf("\n", pos);
      const atLastLine = nextNewline === -1;

      const entries = historyState.entries;
      if (!entries || entries.length === 0) return;

      if (event.key === "ArrowUp") {
        if (!atFirstLine) return;
        setHistoryState(prev => {
          const nextIndex =
            prev.index === -1
              ? 0
              : Math.min(prev.index + 1, entries.length - 1);
          const nextPreDraft = prev.index === -1 ? value : prev.preDraft;
          const resolved = entries[entries.length - 1 - nextIndex] || "";
          applyTextareaValue(resolved);
          event.preventDefault();
          event.stopPropagation();
          return { entries, index: nextIndex, preDraft: nextPreDraft };
        });
        return;
      }

      if (event.key === "ArrowDown") {
        if (!atLastLine) return;
        setHistoryState(prev => {
          if (prev.index === -1) return prev; // not in history mode
          if (prev.index > 0) {
            const nextIndex = prev.index - 1;
            const resolved = entries[entries.length - 1 - nextIndex] || "";
            applyTextareaValue(resolved);
            event.preventDefault();
            event.stopPropagation();
            return { entries, index: nextIndex, preDraft: prev.preDraft };
          }
          // leaving history mode
          const persistedDraft = getAiPromptDraft();
          const draft = prev.preDraft || persistedDraft || "";
          applyTextareaValue(draft);
          event.preventDefault();
          event.stopPropagation();
          return { entries, index: -1, preDraft: "" };
        });
      }
    },
    [aiState.isLoading, mergeActive, historyState.entries, applyTextareaValue]
  );

  useLayoutEffect(() => {
    adjustTextarea();
  }, [adjustTextarea, aiState.text]);

  useEffect(() => {
    // observe size for CodeMirror re-measure
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => requestMeasure());
    ro.observe(el);
    // initial measure after mount
    requestAnimationFrame(() => requestMeasure());
    return () => {
      ro.disconnect();
    };
  }, [requestMeasure]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      e.preventDefault();
      handleCancel();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [handleCancel]);

  return (
    <div
      ref={rootRef}
      className={`${styles.widget}`}
      onKeyDownCapture={handleRootKeyDown}
      data-testid="ai-assistant-root"
    >
      <div className={cn(styles.contaner, "cm-ai-assistant-widget-container")}>
        <div className={styles.body}>
          <div className={styles.textareaContainer}>
            <textarea
              ref={textareaRef}
              className={`cm-ai-assistant-textarea ${styles.textarea}`}
              defaultValue={aiState.text}
              disabled={!!aiState.isLoading || mergeActive}
              placeholder={t(
                historyState.entries && historyState.entries.length > 0
                  ? "editor.ai_widget.placeholder_history_up"
                  : "editor.ai_widget.placeholder_default"
              )}
              rows={1}
              onChange={handleTextareaInput}
              onKeyDown={handleTextareaHistoryKeys}
              data-testid="ai-assistant-textarea"
            />
          </div>
          <div className={styles.buttons}>
            <div
              role="button"
              tabIndex={0}
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleCancel}
              data-testid="ai-assistant-cancel"
            >
              <span className={styles.text}>{cancelLabel.text}</span>
              <span className={styles.keyHint}>{cancelLabel.hint}</span>
            </div>

            <div
              role="button"
              tabIndex={0}
              ref={primaryButtonRef}
              className={`${styles.button} ${aiState.isLoading ? styles.loading : ""} ${disablePrimary ? styles.disabled : ""}`}
              aria-disabled={disablePrimary ? "true" : "false"}
              style={{ cursor: disablePrimary ? "not-allowed" : "pointer" }}
              onClick={disablePrimary ? undefined : handlePrimary}
              data-testid="ai-assistant-primary"
            >
              <span className={styles.text}>{primaryLabel.text}</span>
              {primaryLabel.hint ? (
                <span className={styles.keyHint}>{primaryLabel.hint}</span>
              ) : null}
            </div>
          </div>
        </div>

        {aiState.aiResponse || aiState.lastError ? (
          <div className={styles.footer}>
            <div
              className={styles.errorContainer}
              style={{ display: aiState.lastError ? "block" : "none" }}
            >
              {aiState.lastError || ""}
            </div>
            <div
              className={styles.explanation}
              style={{
                display:
                  aiState.aiResponse && !aiState.lastError ? "flex" : "none",
              }}
            >
              <p>{aiState.aiResponse || ""}</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
